import type { WidgetMode, WidgetState } from "../types";

const DEFAULT_MODE: WidgetMode = "fullscreen";

function createInitialState(mode: WidgetMode = DEFAULT_MODE): WidgetState {
  return {
    isOpen: false,
    isReady: false,
    isStreaming: false,
    isClosed: false,
    mode,
    messageCount: 0,
    conversationId: undefined,
  };
}

export class WidgetStateManager {
  private state: WidgetState;
  private readonly initialMode: WidgetMode;

  constructor(mode: WidgetMode = DEFAULT_MODE) {
    this.initialMode = mode;
    this.state = createInitialState(mode);
  }

  getState(): Readonly<WidgetState> {
    return { ...this.state };
  }

  setIsOpen(isOpen: boolean): void {
    this.state = { ...this.state, isOpen };
  }

  setIsReady(isReady: boolean): void {
    this.state = { ...this.state, isReady };
  }

  setIsStreaming(isStreaming: boolean): void {
    this.state = { ...this.state, isStreaming };
  }

  setIsClosed(isClosed: boolean): void {
    this.state = { ...this.state, isClosed };
  }

  setMode(mode: WidgetMode): void {
    this.state = { ...this.state, mode };
  }

  setMessageCount(messageCount: number): void {
    this.state = { ...this.state, messageCount: Math.max(0, messageCount) };
  }

  incrementMessageCount(by = 1): void {
    this.state = {
      ...this.state,
      messageCount: Math.max(0, this.state.messageCount + by),
    };
  }

  setConversationId(conversationId: string | undefined): void {
    this.state = { ...this.state, conversationId };
  }

  /**
   * Merge a partial state update from the iframe. Only touches fields that the
   * iframe is the source of truth for; SDK-owned fields (`isOpen`, `isReady`)
   * are preserved.
   */
  mergeRemoteState(
    partial: Partial<
      Pick<
        WidgetState,
        "isStreaming" | "isClosed" | "mode" | "messageCount" | "conversationId"
      >
    >
  ): void {
    this.state = { ...this.state, ...partial };
  }

  isOpen(): boolean {
    return this.state.isOpen;
  }

  isReady(): boolean {
    return this.state.isReady;
  }

  isStreaming(): boolean {
    return this.state.isStreaming;
  }

  isClosed(): boolean {
    return this.state.isClosed;
  }

  getMode(): WidgetMode {
    return this.state.mode;
  }

  getMessageCount(): number {
    return this.state.messageCount;
  }

  getConversationId(): string | undefined {
    return this.state.conversationId;
  }

  reset(): void {
    this.state = createInitialState(this.initialMode);
  }
}
